/**
 * Google Address Autocomplete Component for Ant Design
 * Integrates Google Places Autocomplete for address input
 * Uses Ant Design Input component (no raw HTML elements)
 */

import { useEffect, useRef, useState } from "react";
import { Input, Spin } from "antd";
import { loadGoogleMapsScript } from "@/shared/utils/loadGoogleMaps.js";
import { logWarn, logInfo } from "@/shared/utils/logger.js";
import {
  GOOGLE_PLACES_AUTOCOMPLETE_OPTIONS,
  applySelectedAddressToInput,
  placeToAddressData,
} from "@/shared/utils/googlePlacesAddress.js";

/**
 * Google Address Autocomplete Component for Ant Design
 * @param {Object} props
 * @param {string} props.value - Current address value
 * @param {Function} props.onChange - Callback when address text changes (receives event object)
 * @param {Function} props.onPlaceSelect - Callback when a place is selected from autocomplete
 * @param {string} props.placeholder - Input placeholder
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {boolean} props.autoFocus - Whether to auto-focus the input
 * @param {Object} props.inputProps - Additional props to pass to Input
 */
export function GoogleAddressAutocompleteAntd({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "Street, suburb, mall, or area...",
  disabled = false,
  autoFocus = false,
  ...inputProps
}) {
  const [inputValue, setInputValue] = useState(value || "");
  const [isLoading, setIsLoading] = useState(false);
  const autocompleteRef = useRef(null);
  const inputElementRef = useRef(null);
  const isSettingFromPlaceSelectionRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const onPlaceSelectRef = useRef(onPlaceSelect);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onPlaceSelectRef.current = onPlaceSelect;
  }, [onPlaceSelect]);

  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 15;

    const initializeAutocomplete = async () => {
      try {
        await loadGoogleMapsScript();

        if (!isMounted) return;

        if (!window.google?.maps?.places) {
          logWarn("Google Maps Places API not available");
          return;
        }

        const inputElement = inputElementRef.current;

        if (!inputElement || !(inputElement instanceof HTMLInputElement)) {
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(() => {
              if (isMounted) {
                initializeAutocomplete();
              }
            }, 150);
            return;
          }
          logWarn("Could not find input element for autocomplete after retries", {
            element: inputElement,
            type: typeof inputElement,
          });
          return;
        }

        if (autocompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(
            autocompleteRef.current
          );
        }

        const autocomplete = new window.google.maps.places.Autocomplete(
          inputElement,
          GOOGLE_PLACES_AUTOCOMPLETE_OPTIONS
        );

        autocomplete.addListener("place_changed", () => {
          const selectedText = inputElementRef.current?.value || "";
          const place = autocomplete.getPlace();
          const addressData = placeToAddressData(place, selectedText);

          if (!addressData.address) {
            logWarn("No address found for selected place");
            return;
          }

          setIsLoading(true);
          isSettingFromPlaceSelectionRef.current = true;

          logInfo("📍 [FRONTEND] Google Address selected with coordinates", {
            address: addressData.address,
            coordinates: addressData.coordinates,
            placeId: addressData.googlePlaceId,
            addressConfidence: addressData.addressConfidence,
          });

          setInputValue(addressData.address);
          applySelectedAddressToInput(
            inputElementRef.current,
            addressData.address
          );

          onChangeRef.current?.({
            target: {
              value: addressData.address,
            },
          });

          onPlaceSelectRef.current?.(addressData);

          requestAnimationFrame(() => {
            applySelectedAddressToInput(
              inputElementRef.current,
              addressData.address
            );
          });

          setTimeout(() => {
            isSettingFromPlaceSelectionRef.current = false;
          }, 100);
          setIsLoading(false);
        });

        autocompleteRef.current = autocomplete;
        logInfo("Google Address Autocomplete initialized successfully");
      } catch (initError) {
        logWarn("Failed to initialize Google Address Autocomplete", {
          error: initError.message,
        });
      }
    };

    const timer = setTimeout(() => {
      initializeAutocomplete();
    }, 200);

    return () => {
      isMounted = false;
      clearTimeout(timer);
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(
          autocompleteRef.current
        );
      }
    };
  }, []);

  useEffect(() => {
    if (isSettingFromPlaceSelectionRef.current) {
      return;
    }
    if (value !== undefined && value !== inputValue) {
      setInputValue(value || "");
    }
  }, [value, inputValue]);

  const handleInputChange = (event) => {
    if (isSettingFromPlaceSelectionRef.current) {
      return;
    }
    const newValue = event.target.value;
    setInputValue(newValue);
    onChange?.(event);
  };

  const handleInputRef = (node) => {
    if (node) {
      const actualInput =
        node.input ||
        (node.querySelector ? node.querySelector("input") : null) ||
        node;

      if (actualInput instanceof HTMLInputElement) {
        inputElementRef.current = actualInput;
      } else {
        setTimeout(() => {
          const found = node.querySelector?.("input") || node.input;
          if (found instanceof HTMLInputElement) {
            inputElementRef.current = found;
          }
        }, 0);
      }
    } else {
      inputElementRef.current = null;
    }
  };

  return (
    <Input
      ref={handleInputRef}
      value={inputValue}
      onChange={handleInputChange}
      placeholder={placeholder}
      disabled={disabled || isLoading}
      autoFocus={autoFocus}
      suffix={isLoading ? <Spin size="small" /> : null}
      {...inputProps}
    />
  );
}
